/**
 * Global Sources Service for Client-Side Operations
 * Handles all source CRUD operations using Supabase directly
 */

import { supabase, isSupabaseConfigured, getCurrentSession } from "@/lib/supabase";
import type { Source, InsertSource } from "@shared/schema";

export interface SourceAttachment {
    id: string;
    filename: string;
    url: string;
}

class SourcesService {
    /**
     * Generate searchable text from source data for full-text search
     */
    private generateSearchableText(source: {
        title: string;
        content: string;
        topics?: string[];
        tags?: string[];
        subTopics?: string[];
    }): string {
        const parts: string[] = [];

        if (source.title) {
            parts.push(source.title.toLowerCase());
        }

        if (source.content) {
            // Take first 500 chars of content for search
            parts.push(source.content.substring(0, 500).toLowerCase());
        }

        if (source.topics && source.topics.length > 0) {
            if (typeof source.topics === 'string') {
                source.topics = JSON.parse(source.topics);
            }
            parts.push(...source.topics.map(t => typeof t === 'string' ? t.toLowerCase() : t));
        }

        if (source.subTopics && source.subTopics.length > 0) {
            if (typeof source.subTopics === 'string') {
                source.subTopics = JSON.parse(source.subTopics);
            }
            parts.push(...source.subTopics.map(t => t.toLowerCase()));
        }

        if (source.tags && source.tags.length > 0) {
            if (typeof source.tags === 'string') {
                source.tags = JSON.parse(source.tags);
            }
            parts.push(...source.tags.map(t => t.toLowerCase()));
        }
        return parts.join(" ");
    }

    /**
     * Generate a simple unique ID
     */
    private generateSimpleUniqueId(): string {
        const timestamp = Date.now().toString(36);
        const randomPart = Math.random().toString(36).substring(2, 9);
        return timestamp + randomPart;
    }

    /**
     * Transform database row to Source object (database uses camelCase columns)
     */
    private transformToSource(row: any): Source {
        return {
            id: row.id,
            userId: row.userId,
            title: row.title,
            content: row.content,
            type: row.type,
            url: row.url,
            topics: row.topics || [],
            subTopics: row.subTopics || [],
            tags: row.tags || [],
            linkedThreads: row.linkedThreads || [],
            linkedSpaceIds: row.linkedSpaceIds || [],
            mergedFromSpaceId:
                row.mergedFromSpaceId != null && row.mergedFromSpaceId !== ""
                    ? String(row.mergedFromSpaceId)
                    : undefined,
            linkedTopicIds: row.linkedTopicIds || [],
            googleDriveFileId: row.googleDriveFileId,
            googleDriveFolderId: row.googleDriveFolderId,
            googleDriveUrl: row.googleDriveUrl,
            attachments: row.attachments || [],
            searchable: row.searchable,
            folderId: row.folderId,
            createdAt: row.createdAt,
            updatedAt: row.updatedAt,
        } as Source;
    }

    /**
     * Get current user ID from centralized session (single auth call on load)
     */
    private async getCurrentUserId(): Promise<string> {
        if (!isSupabaseConfigured) {
            throw new Error("Service not configured. Please try again later.");
        }
        const session = await getCurrentSession();
        if (!session?.user) {
            throw new Error("User not authenticated. Please log in.");
        }
        return session.user.id;
    }

    /**
     * Get all sources for the current user.
     * @param spaceId - Optional: filter by sources linked to this space.
     * @param workspaceId - When null/undefined: personal sources (workspace_id IS NULL). When set: sources for that workspace.
     */
    async getSources(spaceId?: string, workspaceId?: string | null): Promise<Source[]> {
        const userId = await this.getCurrentUserId();

        if (spaceId) {
            // Check if user has access to this space (owner or shared)
            const { spaceSharingService } = await import("./spaceSharingService");
            const hasAccess = await spaceSharingService.hasSpaceAccess(spaceId);
            
            if (!hasAccess) {
                // User doesn't have access, return empty array
                return [];
            }

            // Get space owner
            const { data: space } = await supabase
                .from("mastermind")
                .select("user_id")
                .eq("id", spaceId)
                .single();

            if (!space) {
                return [];
            }

            // Fetch all sources linked to this space (owned by space owner or any user)
            // First get all sources that have this spaceId in linkedSpaceIds
            const { data: allSources, error: allSourcesError } = await supabase
                .from("sources")
                .select("*")
                .order("createdAt", { ascending: false });

            if (allSourcesError) {
                console.error("Error fetching sources:", allSourcesError);
                throw new Error(`Failed to fetch sources: ${allSourcesError.message}`);
            }

            // Filter sources that are linked to this space
            const linkedSources = (allSources || []).filter((source: any) => {
                const linkedSpaceIds = source.linkedSpaceIds || [];
                return Array.isArray(linkedSpaceIds) && linkedSpaceIds.includes(spaceId);
            });

            return linkedSources.map((row: any) => this.transformToSource(row));
        }

        // No spaceId - fetch user's sources for current context (personal or workspace)
        let query = supabase
            .from("sources")
            .select("*")
            .eq("userId", userId);
        if (workspaceId != null && workspaceId !== "") {
            query = query.eq("workspace_id", workspaceId);
        } else {
            query = query.is("workspace_id", null);
        }
        const { data, error } = await query.order("createdAt", { ascending: false });

        if (error) {
            console.error("Error fetching sources:", error);
            throw new Error(`Failed to fetch sources: ${error.message}`);
        }

        return (data || []).map((row: any) => this.transformToSource(row));
    }

    /**
     * Get a single source by ID
     */
    async getSource(id: string): Promise<Source | null> {
        const userId = await this.getCurrentUserId();

        const { data, error } = await supabase
            .from("sources")
            .select("*")
            .eq("id", id)
            .eq("userId", userId)
            .single();

        if (error) {
            if (error.code === "PGRST116") {
                // No rows returned
                return null;
            }
            console.error("Error fetching source:", error);
            throw new Error(`Failed to fetch source: ${error.message}`);
        }

        if (!data) return null;

        return this.transformToSource(data);
    }

    /**
     * Create a new source.
     * @param workspaceId - When null/undefined: personal. When set: source belongs to that workspace.
     */
    async createSource(sourceData: InsertSource, workspaceId?: string | null): Promise<Source> {
        const userId = await this.getCurrentUserId();

        // Generate searchable text
        const searchable = this.generateSearchableText({
            title: sourceData.title,
            content: sourceData.content,
            topics: sourceData.topics || [],
            tags: sourceData.tags || [],
            subTopics: sourceData.subTopics || [],
        });

        // Use camelCase for database columns (matching Supabase schema)
        const sourceToInsert: any = {
            id: this.generateSimpleUniqueId(),
            userId: userId,
            title: sourceData.title,
            content: sourceData.content,
            type: sourceData.type,
            url: sourceData.url || null,
            topics: sourceData.topics || [],
            subTopics: sourceData.subTopics || [],
            tags: sourceData.tags || [],
            linkedThreads: sourceData.linkedThreads || [],
            linkedSpaceIds: (sourceData as any).linkedSpaceIds || [],
            googleDriveFileId: sourceData.googleDriveFileId || null,
            googleDriveFolderId: sourceData.googleDriveFolderId || null,
            googleDriveUrl: sourceData.googleDriveUrl || null,
            attachments: (sourceData as any).attachments || [],
            folderId: (sourceData as any).folderId || null,
            searchable,
            // createdAt and updatedAt will be handled by database defaults
        };
        if (workspaceId != null && workspaceId !== "") {
            sourceToInsert.workspace_id = workspaceId;
        } else {
            sourceToInsert.workspace_id = null;
        }

        const { data, error } = await supabase
            .from("sources")
            .insert(sourceToInsert)
            .select()
            .single();

        if (error) {
            console.error("Error creating source:", error);
            throw new Error(`Failed to create source: ${error.message}`);
        }

        if (!data) {
            throw new Error("Failed to create source: No data returned");
        }

        return this.transformToSource(data);
    }

    /**
     * Update an existing source
     */
    async updateSource(
        id: string,
        updates: Partial<InsertSource>
    ): Promise<Source | null> {
        const userId = await this.getCurrentUserId();

        // First, get the existing source to generate searchable text
        const existing = await this.getSource(id);
        if (!existing) {
            return null;
        }

        // Merge updates with existing data for searchable generation
        const mergedData = {
            ...existing,
            ...updates,
        };

        // Regenerate searchable text if any searchable fields changed
        const searchable = this.generateSearchableText({
            title: mergedData.title,
            content: mergedData.content,
            topics: mergedData.topics || [],
            tags: mergedData.tags || [],
            subTopics: mergedData.subTopics || [],
        });

        // Use camelCase for database columns (matching Supabase schema)
        const updateData: any = {
            searchable,
            updatedAt: new Date().toISOString(),
        };

        if (updates.title !== undefined) updateData.title = updates.title;
        if (updates.content !== undefined) updateData.content = updates.content;
        if (updates.type !== undefined) updateData.type = updates.type;
        if (updates.url !== undefined) updateData.url = updates.url;
        if (updates.topics !== undefined) updateData.topics = updates.topics;
        if (updates.subTopics !== undefined) updateData.subTopics = updates.subTopics;
        if (updates.tags !== undefined) updateData.tags = updates.tags;
        if (updates.linkedThreads !== undefined) updateData.linkedThreads = updates.linkedThreads;
        if ((updates as any).linkedSpaceIds !== undefined) updateData.linkedSpaceIds = (updates as any).linkedSpaceIds;
        if ((updates as any).linkedTopicIds !== undefined) updateData.linkedTopicIds = (updates as any).linkedTopicIds;
        if (updates.googleDriveFileId !== undefined) updateData.googleDriveFileId = updates.googleDriveFileId;
        if (updates.googleDriveFolderId !== undefined) updateData.googleDriveFolderId = updates.googleDriveFolderId;
        if (updates.googleDriveUrl !== undefined) updateData.googleDriveUrl = updates.googleDriveUrl;
        if ((updates as any).folderId !== undefined) updateData.folderId = (updates as any).folderId;
        if ((updates as any).attachments !== undefined) {
            updateData.attachments = (updates as any).attachments;
        }

        const { data, error } = await supabase
            .from("sources")
            .update(updateData)
            .eq("id", id)
            .eq("userId", userId)
            .select()
            .single();

        if (error) {
            if (error.code === "PGRST116") {
                // No rows returned
                return null;
            }
            console.error("Error updating source:", error);
            throw new Error(`Failed to update source: ${error.message}`);
        }

        if (!data) return null;

        return this.transformToSource(data);
    }

    /**
     * Delete a source
     */
    async deleteSource(id: string): Promise<boolean> {
        const userId = await this.getCurrentUserId();

        const { error } = await supabase
            .from("sources")
            .delete()
            .eq("id", id)
            .eq("userId", userId);

        if (error) {
            console.error("Error deleting source:", error);
            throw new Error(`Failed to delete source: ${error.message}`);
        }

        return true;
    }

    /**
     * Search sources by query (full-text search on searchable field)
     */
    async searchSources(query: string): Promise<Source[]> {
        const userId = await this.getCurrentUserId();

        const { data, error } = await supabase
            .from("sources")
            .select("*")
            .eq("userId", userId)
            .ilike("searchable", `%${query.toLowerCase()}%`)
            .order("createdAt", { ascending: false });

        if (error) {
            console.error("Error searching sources:", error);
            throw new Error(`Failed to search sources: ${error.message}`);
        }

        return (data || []).map((row: any) => this.transformToSource(row));
    }

    /**
     * Upload a file to Supabase Storage
     * Files are stored at path: files/{fileId}
     */
    async uploadFile(file: File | Blob, filename: string, mimeType?: string): Promise<{ id: string; filename: string; url: string }> {
        await this.getCurrentUserId(); // Verify user is authenticated
        const bucketName = import.meta.env.VITE_SUPABASE_STORAGE_BUCKET || "files";
        
        // Generate a unique file ID
        const fileId = this.generateSimpleUniqueId();
        const filePath = `files/${fileId}`;

        console.log(`[SourcesService] Uploading file - filename: ${filename}, path: ${filePath}, bucket: ${bucketName}`);

        // Convert File/Blob to ArrayBuffer
        const arrayBuffer = await file.arrayBuffer();
        const fileBuffer = new Uint8Array(arrayBuffer);

        // Upload to Supabase Storage
        const { error } = await supabase.storage
            .from(bucketName)
            .upload(filePath, fileBuffer, {
                contentType: mimeType || file.type || "application/octet-stream",
                cacheControl: "3600",
                upsert: false,
            });

        if (error) {
            console.error(`[SourcesService] Supabase Storage upload error for path ${filePath}:`, error);
            throw new Error(`Failed to upload file: ${error.message || "Unknown error"}`);
        }

        // Get the public URL for the uploaded file
        const { data: urlData } = supabase.storage
            .from(bucketName)
            .getPublicUrl(filePath);

        const url = urlData?.publicUrl || "";

        console.log(`[SourcesService] Successfully uploaded file: ${fileId}, URL: ${url}`);
        
        return {
            id: fileId,
            filename,
            url,
        };
    }

    /**
     * Get a signed URL for a file stored in Supabase Storage
     * Files are stored at path: files/{fileId}
     */
    async getFileUrl(fileId: string): Promise<string> {
        // Get bucket name from environment or use default
        const bucketName = import.meta.env.VITE_SUPABASE_STORAGE_BUCKET || "files";
        const filePath = `files/${fileId}`;

        console.log(`[SourcesService] Getting file URL - fileId: ${fileId}, path: ${filePath}, bucket: ${bucketName}`);

        // Create a signed URL for secure, time-limited access (24 hours)
        const { data, error } = await supabase.storage
            .from(bucketName)
            .createSignedUrl(filePath, 60 * 60 * 24);
// Use the JS library to download a file.


        if (error) {
            console.error(`[SourcesService] Supabase createSignedUrl error for path ${filePath}:`, error);
            // Check if it's a "not found" error
            if (error.message?.includes("not found") || (error as any).statusCode === 404) {
                throw new Error("File not found");
            }
            throw new Error(`Failed to generate file URL: ${error.message || "Unknown error"}`);
        }

        if (!data?.signedUrl) {
            console.error(`[SourcesService] No signed URL returned for path ${filePath}`);
            throw new Error("Failed to generate file URL: No URL returned");
        }

        console.log(`[SourcesService] Successfully generated signed URL for file: ${fileId}`);
        return data.signedUrl;
    }

    /**
     * Refresh attachment URL for a file
     * This is a convenience method that wraps getFileUrl
     */
    async refreshAttachmentUrl(attachmentId: any): Promise<string | null> {
        try {
            console.log(attachmentId?.id);
            const { data } = await supabase.storage.from('files/files').getPublicUrl(attachmentId?.id)
            console.log(data);
            console.log(data.publicUrl);
            if (data && data.publicUrl) {
                return data?.publicUrl;
            }
            return null;
        } catch (error: any) {
            console.error("[SourcesService] Error refreshing attachment URL:", error);
            // Return null instead of throwing to allow graceful handling
            return null;
        }
    }

    /**
     * Get a source by ID (alias for getSource, for consistency with other services)
     */
    async getSourceById(id: string): Promise<Source | null> {
        return this.getSource(id);
    }

    /**
     * Get all sources linked to a specific thread
     * Searches for sources where linkedThreads array contains the threadId
     */
    async getSourcesByThreadId(threadId: string): Promise<Source[]> {
        const userId = await this.getCurrentUserId();
        const threadIdStr = String(threadId);

        const { data: allData, error: allError } = await supabase
            .from("sources")
            .select("*")
            .eq("userId", userId)
            .order("createdAt", { ascending: false });

        if (allError) {
            console.error("Error fetching sources by thread ID:", allError);
            throw new Error(`Failed to fetch sources by thread ID: ${allError.message}`);
        }

        const filtered = (allData || []).filter((row: any) => {
            const linkedThreads = row.linkedThreads;
            if (Array.isArray(linkedThreads)) {
                return linkedThreads.includes(threadIdStr);
            }
            if (typeof linkedThreads === "string" && linkedThreads.trim()) {
                try {
                    const parsed = JSON.parse(linkedThreads);
                    if (Array.isArray(parsed)) {
                        return parsed.includes(threadIdStr);
                    }
                } catch {
                    return linkedThreads === threadIdStr;
                }
            }
            return false;
        });

        return filtered.map((row: any) => this.transformToSource(row));
    }

    /**
     * Get all sources in a specific folder
     */
    async getSourcesByFolderId(folderId: string | null): Promise<Source[]> {
        const userId = await this.getCurrentUserId();

        const query = folderId
            ? supabase.from("sources").select("*").eq("folderId", folderId).eq("userId", userId)
            : supabase.from("sources").select("*").is("folderId", null).eq("userId", userId);

        const { data, error } = await query.order("createdAt", { ascending: false });

        if (error) {
            console.error("Error fetching sources by folder ID:", error);
            throw new Error(`Failed to fetch sources by folder ID: ${error.message}`);
        }

        return (data || []).map((row: any) => this.transformToSource(row));
    }

    /**
     * Get all sources linked to a specific space
     */
    async getSourcesBySpaceId(spaceId: string): Promise<Source[]> {
        const userId = await this.getCurrentUserId();

        const { data, error } = await supabase
            .from("sources")
            .select("*")
            .eq("userId", userId)
            .contains("linkedSpaceIds", [spaceId])
            .order("createdAt", { ascending: false });

        if (error) {
            console.error("Error fetching sources by space ID:", error);
            throw new Error(`Failed to fetch sources by space ID: ${error.message}`);
        }

        return (data || []).map((row: any) => this.transformToSource(row));
    }

    /**
     * Get all sources linked to a specific topic
     */
    async getSourcesByTopicId(topicId: string): Promise<Source[]> {
        const userId = await this.getCurrentUserId();

        const { data, error } = await supabase
            .from("sources")
            .select("*")
            .eq("userId", userId)
            .contains("linkedTopicIds", [topicId])
            .order("createdAt", { ascending: false });

        if (error) {
            console.error("Error fetching sources by topic ID:", error);
            throw new Error(`Failed to fetch sources by topic ID: ${error.message}`);
        }

        return (data || []).map((row: any) => this.transformToSource(row));
    }

    /**
     * Link a source to a thread
     */
    async linkSourceToThread(sourceId: string, threadId: string): Promise<void> {
        const source = await this.getSource(sourceId);
        if (!source) throw new Error("Source not found");

        const linkedThreads = source.linkedThreads || [];
        if (!linkedThreads.includes(threadId)) {
            await this.updateSource(sourceId, {
                linkedThreads: [...linkedThreads, threadId],
            });
        }
    }

    /**
     * Unlink a source from a thread
     */
    async unlinkSourceFromThread(sourceId: string, threadId: string): Promise<void> {
        const source = await this.getSource(sourceId);
        if (!source) throw new Error("Source not found");

        const linkedThreads = (source.linkedThreads || []).filter((id: string) => id !== threadId);
        await this.updateSource(sourceId, { linkedThreads });
    }

    /**
     * Link a source to a space
     */
    async linkSourceToSpace(sourceId: string, spaceId: string): Promise<void> {
        const source = await this.getSource(sourceId);
        if (!source) throw new Error("Source not found");

        const linkedSpaceIds = (source as any).linkedSpaceIds || [];
        if (!linkedSpaceIds.includes(spaceId)) {
            await this.updateSource(sourceId, {
                linkedSpaceIds: [...linkedSpaceIds, spaceId],
            } as any);
        }
    }

    /**
     * Unlink a source from a space
     */
    async unlinkSourceFromSpace(sourceId: string, spaceId: string): Promise<void> {
        const source = await this.getSource(sourceId);
        if (!source) throw new Error("Source not found");

        const linkedSpaceIds = ((source as any).linkedSpaceIds || []).filter((id: string) => id !== spaceId);
        await this.updateSource(sourceId, { linkedSpaceIds } as any);
    }

    /**
     * Link a source to a topic
     */
    async linkSourceToTopic(sourceId: string, topicId: string): Promise<void> {
        const source = await this.getSource(sourceId);
        if (!source) throw new Error("Source not found");

        const linkedTopicIds = (source as any).linkedTopicIds || [];
        if (!linkedTopicIds.includes(topicId)) {
            await this.updateSource(sourceId, {
                linkedTopicIds: [...linkedTopicIds, topicId],
            } as any);
        }
    }

    /**
     * Unlink a source from a topic
     */
    async unlinkSourceFromTopic(sourceId: string, topicId: string): Promise<void> {
        const source = await this.getSource(sourceId);
        if (!source) throw new Error("Source not found");

        const linkedTopicIds = ((source as any).linkedTopicIds || []).filter((id: string) => id !== topicId);
        await this.updateSource(sourceId, { linkedTopicIds } as any);
    }

    /**
     * Move a source to a folder
     */
    async moveSourceToFolder(sourceId: string, folderId: string | null): Promise<void> {
        await this.updateSource(sourceId, { folderId } as any);
    }
}

// Export singleton instance
export const sourcesService = new SourcesService();

// Export types for use in components
export type { Source, InsertSource, SourceAttachment };


